<template>
    <v-card
        v-if="summary && summary.is_manager"
        class="pa-4 manager-summary-card"
        outlined
    >
        <div class="d-flex align-center mb-2">
            <v-icon color="primary" class="mr-2">mdi-account-tie-outline</v-icon>
            <div class="text-subtitle-1 font-weight-bold">Manager dashboard</div>
            <v-spacer />
            <v-chip x-small color="primary" outlined>{{ summary.staff_count }} staff</v-chip>
        </div>

        <!-- Sections / units this user heads, with a role badge per row.
             Mahmood Saleem has 91 grants; capped + expandable so the card
             doesn't dominate the home screen for high-authority users. -->
        <div v-if="summary.scopes && summary.scopes.length" class="mb-3">
            <div class="caption grey--text mb-1">Your authority</div>
            <div class="d-flex flex-wrap" style="gap: 4px">
                <v-chip
                    v-for="(s, idx) in displayedScopes"
                    :key="idx"
                    x-small outlined
                    :color="roleColor(s.role_code)"
                    v-tooltip="roleLabel(s.role_code)"
                >
                    <span class="font-weight-bold mr-1">{{ s.role_code }}</span>
                    {{ scopeLabel(s) }}
                </v-chip>
                <v-chip
                    v-if="summary.scopes.length > scopeLimit"
                    x-small outlined
                    color="grey"
                    @click="expanded = !expanded"
                >
                    {{ expanded ? 'show fewer' : `+${summary.scopes.length - scopeLimit} more` }}
                </v-chip>
            </div>
        </div>

        <div v-if="summary.pending_total > 0" class="d-flex flex-wrap mb-3" style="gap: 6px">
            <v-chip
                v-for="b in summary.pending_breakdown"
                :key="b.slug"
                x-small
                :color="b.count > 0 ? 'amber darken-2' : 'grey lighten-2'"
                :text-color="b.count > 0 ? 'white' : ''"
                @click="goToQueue(b.slug)"
            >
                <v-icon x-small left>{{ b.icon }}</v-icon>
                {{ b.label }} {{ b.count }}
            </v-chip>
            <v-chip
                v-if="summary.on_leave_today_count > 0"
                x-small color="blue-grey lighten-2"
            >
                <v-icon x-small left>mdi-island</v-icon>
                On leave today {{ summary.on_leave_today_count }}
            </v-chip>
        </div>
        <div v-else class="caption text--secondary mb-3">
            No items waiting on you right now.
        </div>

        <v-btn
            small
            color="primary"
            :to="summary.link"
            block
        >
            <v-icon left small>mdi-arrow-right</v-icon>
            Open my team
        </v-btn>
    </v-card>
</template>

<script>
export default {
    name: 'ManagerSummaryCard',
    data: () => ({
        summary: null,
        expanded: false,
        scopeLimit: 6,
    }),
    computed: {
        displayedScopes() {
            const all = this.summary?.scopes || []
            return this.expanded ? all : all.slice(0, this.scopeLimit)
        },
    },
    mounted() {
        this.load()
    },
    methods: {
        load() {
            this.$axios.get('/api/dashboard/manager-summary').then((resp) => {
                this.summary = resp.data
            }).catch(() => {
                this.summary = { is_manager: false }
            })
        },
        goToQueue(slug) {
            this.$router.push({ path: this.summary.link, query: { tab: 'approvals', q: slug } })
        },
        scopeLabel(s) {
            // Section · Unit when both, otherwise whichever exists.
            if (s.section_name && s.unit_name) return `${s.section_name} · ${s.unit_name}`
            return s.unit_name || s.section_name || '—'
        },
        roleLabel(code) {
            return ({
                SH: 'Section head',
                UH: 'Unit head',
                OH: 'Overall head of this unit',
                AH: 'Overall head (all-head level)',
            })[code] || code
        },
        roleColor(code) {
            return ({
                SH: 'indigo',
                UH: 'teal',
                OH: 'deep-purple',
                AH: 'red darken-1',
            })[code] || 'grey'
        },
    },
}
</script>

<style scoped>
.manager-summary-card {
    border-left: 4px solid var(--v-primary-base);
}
</style>
